import React, { useContext, useState } from 'react';
import { UserContext } from '../context/UserContext';
import { useNavigate } from 'react-router-dom';
import { resolveMfaSignIn } from '../services/SecurityService.mjs';
import "../css/QdexAuth.css"; // Centralized auth styles

const LoginForm = () => {
    const { loginGoogle, loginEmail, signupEmail, resetPassword } = useContext(UserContext);
    const navigate = useNavigate();

    const [isSignUp, setIsSignUp] = useState(false);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    // M1: password reset. `notice` carries the deliberately generic confirmation —
    // it must read the same whether or not the address has an account.
    const [notice, setNotice] = useState("");
    const [resetting, setResetting] = useState(false);
    // S2: set when Firebase interrupts sign-in for the second factor. Holds the
    // original error, which carries the resolver session.
    const [mfaError, setMfaError] = useState(null);
    const [mfaCode, setMfaCode] = useState("");

    const handleForgotPassword = async () => {
        setError("");
        setNotice("");

        if (!email.trim()) {
            setError("Enter your email address first, then click “Forgot password?”.");
            return;
        }

        setResetting(true);
        try {
            await resetPassword(email.trim());
            setNotice("If an account exists for that address, a reset link is on its way. Check your inbox and spam folder.");
        } catch (err) {
            console.error("Password reset failed:", err);
            setError("Couldn't send a reset email right now. Please try again in a moment.");
        } finally {
            setResetting(false);
        }
    };

    const handleGoogleLogin = async () => {
        setIsLoading(true);
        setError("");
        try {
            await loginGoogle();
            navigate('/');
        } catch (err) {
            // A Google sign-in can hit the second-factor challenge too.
            if (err.code === 'auth/multi-factor-auth-required') {
                setMfaError(err);
            } else {
                setError("Google Login failed. Please try again.");
                console.error(err);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setIsLoading(true);

        try {
            if (isSignUp) {
                await signupEmail(email, password);
            } else {
                await loginEmail(email, password);
            }
        } catch (err) {
            console.error("Auth Error:", err.code);
            // S2: an account with a second factor doesn't fail here — Firebase
            // throws this to say "credentials were right, now prove the second
            // factor". Without handling it, enrolling in 2FA would lock the user
            // out of their own account.
            if (err.code === 'auth/multi-factor-auth-required') {
                setMfaError(err);
            } else if (err.code === 'auth/email-already-in-use') {
                setError("That email is already registered.");
            } else if (err.code === 'auth/wrong-password' || err.code === 'auth/user-not-found') {
                setError("Invalid email or password.");
            } else if (err.code === 'auth/weak-password') {
                setError("Password should be at least 6 characters.");
            } else {
                setError("Failed to sign in. Check your connection.");
            }
        } finally {
            setIsLoading(false);
        }
    };

    /** Complete a sign-in that stopped at the second-factor challenge. */
    const handleMfaSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setIsLoading(true);
        try {
            await resolveMfaSignIn(mfaError, mfaCode.trim());
            // onAuthStateChanged takes it from here.
        } catch (err) {
            console.error("MFA Error:", err.code);
            setError(
                err.code === 'auth/invalid-verification-code'
                    ? "That code isn't right. Check your authenticator app and try again."
                    : "Couldn't verify that code. Please try again."
            );
        } finally {
            setIsLoading(false);
        }
    };

    // S2 — the second-factor challenge replaces the form entirely: the password
    // step already succeeded, and showing it again would invite a re-submit that
    // discards the resolver session this screen depends on.
    if (mfaError) {
        return (
            <div className="qdex-auth-wrapper">
                <div className="qdex-auth-card">
                    <div className="qdex-brand">
                        <h1 className="qdex-logo">qdex</h1>
                        <p className="qdex-subtitle">Two-factor authentication</p>
                    </div>

                    <h2>Enter your code</h2>
                    <p className="qdex-lead">
                        Open your authenticator app and enter the 6-digit code for QDex.
                    </p>

                    {error && (
                        <div className="qdex-error-message">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="12" y1="8" x2="12" y2="12"></line>
                                <line x1="12" y1="16" x2="12.01" y2="16"></line>
                            </svg>
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleMfaSubmit} className="qdex-form">
                        <div className="input-group">
                            <input
                                type="text"
                                className="qdex-input"
                                placeholder="123456"
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                maxLength={8}
                                value={mfaCode}
                                onChange={(e) => setMfaCode(e.target.value)}
                                required
                                autoFocus
                                disabled={isLoading}
                            />
                        </div>
                        <button type="submit" className="qdex-btn qdex-btn-primary" disabled={isLoading || !mfaCode.trim()}>
                            {isLoading ? "Verifying…" : "Verify"}
                        </button>
                    </form>

                    <p className="qdex-footer-text">
                        <button
                            type="button"
                            className="qdex-text-btn"
                            onClick={() => { setMfaError(null); setMfaCode(""); setError(""); }}
                            disabled={isLoading}
                        >
                            Back to sign in
                        </button>
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="qdex-auth-wrapper">
            <div className="qdex-auth-card">
                <div className="qdex-brand">
                    <h1 className="qdex-logo">qdex</h1>
                    <p className="qdex-subtitle">Asset Management</p>
                </div>

                <h2>{isSignUp ? "Create Account" : "Welcome Back"}</h2>

                {error && (
                    <div className="qdex-error-message">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="12" y1="8" x2="12" y2="12"></line>
                            <line x1="12" y1="16" x2="12.01" y2="16"></line>
                        </svg>
                        {error}
                    </div>
                )}

                {notice && (
                    <div className="qdex-notice-message">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                            <polyline points="22 4 12 14.01 9 11.01"></polyline>
                        </svg>
                        {notice}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="qdex-form">
                    <div className="input-group">
                        <input
                            type="email"
                            className="qdex-input"
                            placeholder="Email Address"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            disabled={isLoading}
                        />
                    </div>
                    <div className="input-group">
                        <input
                            type="password"
                            className="qdex-input"
                            placeholder="Password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            disabled={isLoading}
                        />
                    </div>

                    {!isSignUp && (
                        <div className="qdex-form-aside">
                            <button
                                type="button"
                                className="qdex-text-btn"
                                onClick={handleForgotPassword}
                                disabled={isLoading || resetting}
                            >
                                {resetting ? "Sending…" : "Forgot password?"}
                            </button>
                        </div>
                    )}

                    <button type="submit" className="qdex-btn qdex-btn-primary" disabled={isLoading}>
                        {isLoading ? "Processing..." : (isSignUp ? "Sign Up" : "Log In")}
                    </button>
                </form>

                <div className="qdex-divider">
                    <span>OR</span>
                </div>

                <button
                    onClick={handleGoogleLogin}
                    className="qdex-btn qdex-btn-google"
                    disabled={isLoading}
                >
                    <svg viewBox="0 0 24 24" width="18" height="18" xmlns="http://www.w3.org/2000/svg">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                    Continue with Google
                </button>

                <p className="qdex-footer-text">
                    {isSignUp ? "Already have an account?" : "Need an account?"}
                    <button
                        type="button"
                        className="qdex-text-btn"
                        onClick={() => setIsSignUp(!isSignUp)}
                        disabled={isLoading}
                    >
                        {isSignUp ? "Log In" : "Sign Up"}
                    </button>
                </p>
            </div>
        </div>
    );
};

export default LoginForm;